import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseCorporateEveningGlobalFormContainer,
  HouseCorporateEveningGlobalFormContainerProps,
} from './components/form-container/HouseCorporateEveningGlobalFormContainer'
import { HouseCorporateEveningGlobalIsEnabledField } from './components/is-enabled-field/HouseCorporateEveningGlobalIsEnabledField'
import { HouseCorporateEveningGlobalOptionsFields } from './components/options-fields/HouseCorporateEveningGlobalOptionsFields'
import {
  computeHouseCorporateEveningGlobalDefaultValues,
  computeHouseCorporateEveningGlobalInputs,
} from './helpers/compute-house-corporate-evening-global'
import { HouseCorporateEveningGlobalDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseCorporateEveningGlobalFormProps = {
  updateCorporateEveningGlobal: {
    mutate: (formData: HouseCorporateEveningGlobalDto) => void
    isLoading: boolean
  }
}
export const HouseCorporateEveningGlobalForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateCorporateEveningGlobal,
}: HouseCorporateEveningGlobalFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsGlobal') {
    return null
  }

  const { corporateEvening } = options

  const methods = useForm<HouseCorporateEveningGlobalDto>({
    defaultValues:
      computeHouseCorporateEveningGlobalDefaultValues(corporateEvening),
  })
  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateCorporateEveningGlobal

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseCorporateEveningGlobalDto) => {
    const inputs = computeHouseCorporateEveningGlobalInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseCorporateEveningGlobalFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateCorporateEveningGlobal.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseCorporateEveningGlobalFormContainer {...formContainerProps}>
        <HouseCorporateEveningGlobalIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseCorporateEveningGlobalOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseCorporateEveningGlobalFormContainer>
    </FormProvider>
  )
}
